import React, { useState } from "react";
import {
    Paper, Typography, Box, Button, FormControl, InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox, Alert,
} from "@mui/material";
import { startlistPdf, runResultsPdf, eventResultsPdf, toEventLive, enrichLive, telemarkToLive, telemarkEventLive, analysisPdf } from "./racePdf";
import { ensureOwnLogo } from "../../utils/pdfBranding";

// Bloc "Impression PDF" présent dans les 3 exports.
// pointsByBib   : points finaux par dossard, ou null (course populaire)
// pointsMissing : message si les points sont attendus mais pas encore calculés
// onPenaltyPdf  : si fourni, ajoute le bouton "PDF calcul de pénalité"
export default function PrintPanel({
                                       config, eventKey, apiFetch, setError, pointsByBib = null, pointsMissing = "", infoLines = [], onPenaltyPdf,
                                   }) {
    const isPop = config.raceType === "populaire";
    const [run, setRun] = useState(1);
    const [groupByCategory, setGroupByCategory] = useState(isPop);
    const [includeSheet, setIncludeSheet] = useState(!isPop);
    const [busy, setBusy] = useState("");

    const getJson = async (url) => {
        const res = await apiFetch(url);
        if (!res) return null;
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `HTTP ${res.status}`);
        }
        return res.json();
    };
    const key = encodeURIComponent(eventKey);

    const wrap = (name, fn) => async () => {
        setError("");
        setBusy(name);
        try {
            await ensureOwnLogo(); // logo 321-timing du pied de page
            await fn();
        } catch (e) {
            setError(`PDF non généré : ${e.message}`);
        } finally {
            setBusy("");
        }
    };

    const printStartlist = wrap("start", async () => {
        const startlist = await getJson(`/api/timing/startlist/${key}?run=${run}`);
        if (!startlist) return;
        if (!startlist.length) throw new Error(`la liste de départ de la manche ${run} est vide.`);
        startlistPdf({ config, eventKey, run, startlist, groupByCategory, includeSheet });
    });

    // Participants : complètent nom / année / club… dans les résultats.
    const athletes = async () => (await getJson(`/api/timing/athletes/${key}`).catch(() => [])) || [];

    // Télémark : classement avec pénalités (temps de ski + style + saut).
    const isTM = String(config.sector || "").toUpperCase() === "TM";

    const printRun = wrap("run", async () => {
        const src = isTM
            ? telemarkToLive(await getJson(`/api/timing/results-telemark/${key}?run=${run}&view=run`))
            : await getJson(`/api/timing/live/${key}?run=${run}`);
        const live = enrichLive(src, await athletes());
        if (live) runResultsPdf({ config, eventKey, run, live, groupByCategory, includeSheet });
    });

    // Analyse de la manche : secteurs (intermédiaires), temps de ski,
    // pénalités (télémark), temps et écart — 04.11.2026.
    const printAnalysis = wrap("analysis", async () => {
        const ath = await athletes();
        const live = await getJson(`/api/timing/live/${key}?run=${run}`);
        if (!live) return;
        let src = live;
        if (isTM) {
            const tm = await getJson(`/api/timing/results-telemark/${key}?run=${run}&view=run`);
            if (tm) src = { ...tm, notClassified: live.notClassified };
        }
        analysisPdf({ config, eventKey, run, live: enrichLive(src, ath) });
    });

    const printEvent = wrap("event", async () => {
        const ath = await athletes();
        const live = enrichLive(await getJson(`/api/timing/live/${key}`), ath);
        if (!live) return;
        let run1NotClassified = null;
        let eventLive = live;
        if ((live.runCount || 1) > 1) {
            const run1 = enrichLive(await getJson(`/api/timing/live/${key}?run=1`), ath);
            run1NotClassified = run1?.notClassified || [];
            // Classement combiné (temps total + manches) — 07.10.2026.
            eventLive = toEventLive(live, [run1]);
        }
        if (isTM) {
            // Télémark : manches et total avec pénalités, classement télémark.
            const lastRun = live.runCount || 1;
            const tm =
                lastRun > 1
                    ? await telemarkEventLive(getJson, key, lastRun, live.combineMethod)
                    : telemarkToLive(await getJson(`/api/timing/results-telemark/${key}?run=1&view=run`));
            if (tm) eventLive = enrichLive({ ...tm, runCount: lastRun, notClassified: live.notClassified }, ath);
        }
        eventResultsPdf({ config, eventKey, live: eventLive, run1NotClassified, pointsByBib, infoLines, groupByCategory, includeSheet });
    });

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" mb={2}>Impression PDF</Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Manche</InputLabel>
                    <Select label="Manche" value={run} onChange={(e) => setRun(Number(e.target.value))}>
                        {[1, 2, 3].map((n) => <MenuItem key={n} value={n}>Manche {n}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Checkbox checked={groupByCategory} onChange={(e) => setGroupByCategory(e.target.checked)} />}
                    label={
                        config.printSort?.sections?.length
                            ? "Appliquer le Tri impression (sections)"
                            : "Grouper par catégorie (une page par catégorie)"
                    }
                />
                <FormControlLabel
                    control={<Checkbox checked={includeSheet} onChange={(e) => setIncludeSheet(e.target.checked)} />}
                    label="Inclure la fiche de course"
                />
            </Box>

            {pointsMissing && <Alert severity="info" sx={{ mb: 2 }}>{pointsMissing}</Alert>}

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button variant="outlined" disabled={!!busy} onClick={printStartlist}>
                    {busy === "start" ? "Génération…" : `Liste de départ manche ${run}`}
                </Button>
                <Button variant="outlined" disabled={!!busy} onClick={printRun}>
                    {busy === "run" ? "Génération…" : `Résultats manche ${run}`}
                </Button>
                <Button variant="outlined" disabled={!!busy} onClick={printAnalysis}>
                    {busy === "analysis" ? "Génération…" : `Analyse manche ${run}`}
                </Button>
                <Button variant="contained" disabled={!!busy || !!pointsMissing} onClick={printEvent}>
                    {busy === "event" ? "Génération…" : isPop ? "Classement final" : "Résultats de l'épreuve"}
                </Button>
                {onPenaltyPdf && (
                    <Button variant="outlined" disabled={!!busy} onClick={onPenaltyPdf}>
                        Calcul de pénalité
                    </Button>
                )}
            </Box>
        </Paper>
    );
}